import { useState, useMemo } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import AlertDialog from 'components/AlertDialog'
import LoadingBar from 'components/LoadingBar'
import { ReactComponent as ButtonLoading } from 'icons/loading2.svg'
import cutileiLogo from 'icons/logo-cutilei-simple.png'
import * as FA from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Container,
  CardHeader,
  CardBody,
  CardFooter,
  SubTitle,
  InfoText,
  Button,
  Row,
  Seal,
  Column,
  SealsRow,
  SealImage,
  SealDescription,
  MoneyTextMask
} from './styles'

export default function LoyaltyCard ({
  data: card,
  onEdit,
  onDelete: handleDelete
}) {
  const [showAlertDialog, setShowAlertDialog] = useState (false)
  const [deleting, setDeleting] = useState (false)
  const businessId = AuthService.getBusinessId ()
  const hasEditPermission = AuthService.hasPermission ('business_config.loyalty_cards', 'edit')
  const hasDeletePermission = AuthService.hasPermission ('business_config.loyalty_cards', 'delete')
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const handleEdit = () => onEdit (card)

  const deleteLoyaltyCard = async () => {
    setDeleting (true)
    try {
      if (card.customer_id) {
        await cutileiApi.delete (
          `/businesses/${businessId}/customers/${card.customer_id}`
          + `/loyalty_cards/${card.id}`,
          requestConfig
        )
      } else {
        await cutileiApi.delete (
          `/businesses/${businessId}/loyalty_cards/${card.id}`,
          requestConfig
        )
      }

      setDeleting (false)
      handleDelete (card) 
    } catch (error) {
      setDeleting (false)
      logApiError (error)
    }
  }

  const toggleAlertDialog = () => setShowAlertDialog (!showAlertDialog)

  const cardContent = useMemo (() => {
    if (card.type === 'services') {
      return card.services?.map ((service, index) => (
        <Column key={index}>
          <SealDescription>
            <SubTitle>{service.name}</SubTitle>
          </SealDescription>
          <SealsRow count={service.required_amount}>
            {Array.from ({ length: service.required_amount || 0 }).map ((_, sealIndex) => (
              <Seal
                key={sealIndex}
                title={service.name}
                completed={sealIndex < (service.completed_amount || 0)}
              >
                <SealImage
                  src={cutileiLogo}
                  alt={service.name}
                  completed={sealIndex < (service.completed_amount || 0)}
                />
              </Seal>
            ))}
          </SealsRow>
        </Column>
      ))
    }
    if (card.type === 'visits') {
      return (
        <Column>
          <SealDescription>
            <SubTitle>Visitas</SubTitle>
          </SealDescription>
          <SealsRow count={card.required_visits}>
            {Array.from ({ length: card.required_visits || 0 }).map ((_, index) => (
              <Seal
                key={index}
                title='Visita'
                completed={index < (card.visits || 0)}
              >
                <SealImage
                  src={cutileiLogo}
                  alt='Visita'
                  completed={index < (card.visits || 0)}
                />
              </Seal>
            ))}
          </SealsRow>
        </Column>
      )
    }
    if (card.type === 'spent') {
      return card.spent !== undefined ? (
        <LoadingBar
          width={250}
          height={9}
          color='#44CF6C'
          direction='column'
          textMask='currency'
          textStyle={{color: '#252525', fontWeight: 500}}
          currentValue={card.spent || 0}
          totalValue={card.required_spent || 0}
        />
      ) : (
        <Column>
          <SealDescription>
            <SubTitle>Valor de consumo</SubTitle>
          </SealDescription>
          <MoneyTextMask
            value={card.required_spent || 0}
            textStyle={{color: '#252525', fontWeight: 500}}
          />
        </Column>
      )
    }
  }, [card])

  return (
    <Container>
      <CardHeader>
        <SubTitle>
          {card.name || 'Nome do Cartão'}
        </SubTitle>
        {card.id && (
          <Row style={{gap: 3}}>
            {hasEditPermission && !card.customer_id && (
              <Button onClick={handleEdit}>
                <FA.FaEdit color='#35A7FF' size={18}/>
              </Button>
            )}
            {hasDeletePermission && (
              <Button onClick={toggleAlertDialog}>
                {deleting ? (
                  <ButtonLoading style={{margin: 0}}/>
                ) : (
                  <FA.FaTrash color='#FF3939' size={16}/>
                )}
              </Button>
            )}
          </Row>
        )}
      </CardHeader>
      <CardBody>
        {cardContent}
      </CardBody>
      <CardFooter>
        {card.expires_at !== null ? (
          <Row>
            <SubTitle>Vencimento:</SubTitle>
            <InfoText>
              {card.expires_at === ''
                ? 'DD/MM/AAAA'
                : DateTime.fromISO (card.expires_at).toFormat ('dd/MM/yyyy')
              }
            </InfoText>
          </Row>
        ) : (
          <InfoText>Sem vencimento</InfoText>
        )}
      </CardFooter>
      <AlertDialog
        visible={showAlertDialog}
        title='Atenção!'
        message={card.customer_id
          ? 'Deseja realmente excluir este cartão fidelidade deste cliente?'
          : 'Deseja realmente excluir este cartão fidelidade?'
        }
        confirmText='Sim'
        cancelText='Não'
        onConfirm={deleteLoyaltyCard}
        onClose={toggleAlertDialog}
        dangerous
      />
    </Container>
  )
}
